import { AnalyticsService } from '@alison/analytics/client';
import { radius, WorkflowIcon } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useMemo } from 'react';
import { useWorkflowCatalog } from '../../lib/workflows.hooks.js';
import type { AskEvoResult } from './askEvoResult.js';
import { IconBadge } from './IconBadge.js';
import { iconFor } from './workflowIcons.js';
import type { WorkflowCard } from './workflows.types.js';

function annotationsOf(result: AskEvoResult): string[] {
  const lines: string[] = [];
  if (result.window !== null && result.window !== undefined) lines.push(result.window);
  for (const caveat of result.caveats) lines.push(caveat);
  if (result.truncated) lines.push('This answer was truncated.');
  if (result.synthesis_failed) lines.push('Some data could not be synthesized into this answer.');
  return lines;
}

function MatchedWorkflowCard({
  workflowId,
  card,
}: {
  workflowId: string;
  card: WorkflowCard | undefined;
}): ReactNode {
  const navigate = useNavigate();

  return (
    <ButtonBase
      onClick={() => {
        AnalyticsService.track('Ask Alison - Click Workflow', { workflowId });
        void navigate({
          to: '/workflows/$workflowId',
          params: { workflowId },
          search: (previous) => previous,
        });
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        width: '100%',
        borderRadius: `${radius.lg}px`,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        textAlign: 'left',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { borderColor: 'primary.main', boxShadow: (theme) => theme.shadows[1] },
      }}
    >
      <IconBadge icon={iconFor(card?.icon ?? '', WorkflowIcon)} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          {card?.name ?? workflowId}
        </Typography>
        {card?.description === undefined ? null : (
          <Typography
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {card.description}
          </Typography>
        )}
      </Box>
      <ArrowForwardOutlined sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
    </ButtonBase>
  );
}

export function AskEvoAnswerView({ result }: { result: AskEvoResult }): ReactNode {
  const { providerName } = useTenant();
  const { data: catalog } = useWorkflowCatalog();
  const cardsById = useMemo(() => {
    const items = catalog?.groups.flatMap((group) => group.items) ?? [];
    return new Map(items.map((item) => [item.workflowId, item]));
  }, [catalog]);

  if (!result.usable) {
    return (
      <Alert severity="error">
        {result.reason ?? `${providerName} could not answer this question.`}
      </Alert>
    );
  }

  if (result.matched_workflow !== null && result.matched_workflow !== undefined) {
    const hasEntryPoints = result.matched_workflow_entry_points.length > 0;
    const workflowIds = hasEntryPoints
      ? result.matched_workflow_entry_points
      : [result.matched_workflow];

    return (
      <Stack sx={{ gap: 1.5 }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          {hasEntryPoints
            ? "That's part of a workflow that can't be started on its own — start from one of these instead:"
            : 'This looks like a match for an existing workflow:'}
        </Typography>
        <Stack sx={{ gap: 1 }}>
          {workflowIds.map((workflowId) => (
            <MatchedWorkflowCard
              key={workflowId}
              workflowId={workflowId}
              card={cardsById.get(workflowId)}
            />
          ))}
        </Stack>
      </Stack>
    );
  }

  const annotations = annotationsOf(result);

  return (
    <Stack sx={{ gap: 2 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>
        {result.answer}
      </Typography>
      {annotations.length === 0 ? null : (
        <Stack sx={{ gap: 0.5 }}>
          {annotations.map((annotation) => (
            <Typography key={annotation} sx={{ fontSize: 11, color: 'text.secondary' }}>
              {annotation}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
